import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Save, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { onboardingAPI } from '../lib/onboardingAPI';
import PersonalDetailsForm from '../components/onboarding/PersonalDetailsForm';
import SpouseForm from '../components/onboarding/SpouseForm';
import RelatedEntitiesForm from '../components/onboarding/RelatedEntitiesForm';
import IdentityVerificationForm from '../components/onboarding/IdentityVerificationForm';
import IncomeSourcesForm from '../components/onboarding/IncomeSourcesForm';
import RentalPropertiesForm from '../components/onboarding/RentalPropertiesForm';
import InvestmentsForm from '../components/onboarding/InvestmentsForm';
import ForeignIncomeForm from '../components/onboarding/ForeignIncomeForm';
import BusinessForm from '../components/onboarding/BusinessForm';
import GstBasForm from '../components/onboarding/GstBasForm';
import DocumentCentreForm from '../components/onboarding/DocumentCentreForm';
import PreviousAccountantForm from '../components/onboarding/PreviousAccountantForm';

// Sections with a dedicated rich form (Modules 4+). Others use the generic editor below.
const DEDICATED = {
  personal_details: PersonalDetailsForm,
  spouse: SpouseForm,
  related_entities: RelatedEntitiesForm,
  previous_accountant: PreviousAccountantForm,
  identity_verification: IdentityVerificationForm,
  income_sources: IncomeSourcesForm,
  rental_properties: RentalPropertiesForm,
  investments: InvestmentsForm,
  foreign_income: ForeignIncomeForm,
  business_activity: BusinessForm,
  gst_bas: GstBasForm,
  document_uploads: DocumentCentreForm,
};

const SECTION_TITLES = {
  personal_details: 'Personal Details',
  spouse: 'Spouse Details',
  related_entities: 'Related Entities',
  previous_accountant: 'Previous Accountant',
  identity_verification: 'Identity Verification',
  income_sources: 'Income Sources',
  rental_properties: 'Rental Properties',
  investments: 'Investments & CGT',
  foreign_income: 'Foreign Income',
  business_activity: 'Business Activity',
  gst_bas: 'GST, BAS & PAYG withholding',
  document_uploads: 'Document Uploads',
};

// Income types — selecting these reveals the matching detailed sections (rental/investments/foreign/business).
const INCOME_TYPES = [
  { key: 'salary_wages', label: 'Salary and wages' },
  { key: 'rental_property_income', label: 'Rental property income' },
  { key: 'capital_gains', label: 'Capital gains' },
  { key: 'dividend_income', label: 'Dividend income' },
  { key: 'interest_income', label: 'Interest income' },
  { key: 'shares', label: 'Shares' },
  { key: 'managed_funds', label: 'Managed funds' },
  { key: 'crypto_transactions', label: 'Crypto transactions' },
  { key: 'employee_share_schemes', label: 'Employee share schemes' },
  { key: 'foreign_income', label: 'Foreign income' },
  { key: 'sole_trader_income', label: 'Sole trader income' },
  { key: 'business_income', label: 'Business income' },
  { key: 'government_payments', label: 'Government payments' },
  { key: 'other_income', label: 'Other income' },
];

export default function OnboardingSection() {
  const { key } = useParams();
  const navigate = useNavigate();
  const [clientId, setClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');
  const [income, setIncome] = useState([]);
  const [status, setStatus] = useState('not_started');

  const isIncome = key === 'income_sources';
  const title = SECTION_TITLES[key] || 'Onboarding Section';

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const me = await api.get('/api/me');
        const cid = me.data.active_membership?.client_id;
        if (!cid) { toast.error('This account is not linked to an onboarding'); navigate('/onboarding'); return; }
        if (!active) return;
        setClientId(cid);
        if (DEDICATED[key]) return;  // dedicated form fetches its own data
        const res = await onboardingAPI.getSection(cid, key);
        const rd = res.data.response_data || {};
        setNotes(rd.notes || '');
        setIncome(rd.selected_income_types || []);
        setStatus(res.data.status || 'not_started');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load section');
        navigate('/onboarding');
      } finally {
        active && setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [key, navigate]);

  const buildPayload = (newStatus) => {
    const response_data = isIncome ? { selected_income_types: income, notes } : { notes };
    return { response_data, status: newStatus };
  };

  const save = async (newStatus) => {
    setSaving(true);
    try {
      await onboardingAPI.saveSection(clientId, key, buildPayload(newStatus));
      toast.success(newStatus === 'completed' ? 'Section marked complete' : 'Saved — you can return any time');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleIncome = (k) => setIncome((cur) => cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);

  if (loading) {
    return <div className="p-6 max-w-2xl mx-auto"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;
  }

  // Dedicated rich forms (Modules 4+) take over for their sections.
  const Dedicated = DEDICATED[key];
  if (Dedicated) return <Dedicated clientId={clientId} />;

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/onboarding')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to checklist
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{title}</CardTitle>
          <CardDescription>
            {isIncome
              ? 'Select all the income types that apply to you. Your selections reveal the matching detailed sections (rental, investments, foreign income, business).'
              : 'A detailed form for this section is on the way. For now you can add any notes and mark it complete.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {isIncome && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {INCOME_TYPES.map((t) => (
                <label key={t.key} className="flex items-center gap-2 rounded-lg border p-2.5 cursor-pointer hover:bg-muted/50">
                  <Checkbox checked={income.includes(t.key)} onCheckedChange={() => toggleIncome(t.key)} data-testid={`income-${t.key}`} />
                  <span className="text-sm text-foreground/90">{t.label}</span>
                </label>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <Label>Notes {isIncome ? '(optional)' : ''}</Label>
            <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything you'd like your accountant to know about this section…" data-testid="section-notes" />
          </div>

          <div className="flex justify-between items-center pt-2">
            <Button variant="outline" onClick={() => save('in_progress')} disabled={saving} data-testid="section-save">
              <Save className="w-4 h-4 mr-2" /> Save &amp; return later
            </Button>
            <Button onClick={() => save('completed')} disabled={saving} data-testid="section-complete">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Mark as complete
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
